<script lang="ts" setup>
import type { TripOrderStop } from "@/views/trips/edit/trip";
import type { OrderFacilityDto, OrderFreightDto } from "@/api/orders/dto/order.dto";
import { computed, ref } from "vue";
import type { FacilityDto } from "@/api/company/dto/facility.dto";
import type { DispatcherDto } from "@/api/dispatchers/dto/dispatcher.dto";
import { OrderFreightState } from "@/data/order";
import { useAppStore } from "@/store/app.store";
import { API } from "@/api/api";

const appStore = useAppStore();
const api = new API();

const props = withDefaults(
  defineProps<{
    stop: TripOrderStop;
    freights: OrderFreightDto[];
    label?: string;
  }>(),
  {
    label: "Changes",
  },
);

const oldFacility = ref<FacilityDto | OrderFacilityDto | null>(null);
const lastEdited = ref<DispatcherDto | null>(null);

const updatedFreights = computed(() => {
  return props.freights.filter((f) => f.state === OrderFreightState.updated);
});

function onShow(val: boolean) {
  if (val) {
    if (props.stop.lastEditedBy) {
      lastEdited.value = appStore.getDispatcher(props.stop.lastEditedBy);
    }
    setOldFacility();
  }
}

async function setOldFacility() {
  if (oldFacility.value || !props.stop.oldFacilityId) {
    return;
  }

  if (props.stop.oldFacility) {
    oldFacility.value = props.stop.oldFacility;
    return;
  }
  const res = await api.company.findFacilityById(props.stop.oldFacilityId);

  if (res.success && res.data) {
    oldFacility.value = res.data;
  }
}
</script>

<template>
  <v-menu location="top start" offset="4" width="483" @update:model-value="onShow">
    <template #activator="{ props: menuProps }">
      <v-icon
        class="ml-2"
        color="warning"
        data-qa="alert-icon"
        icon="mdi-alert-circle-outline"
        size="20"
        v-bind="menuProps"
      ></v-icon>
    </template>
    <v-card>
      <v-card-text>
        <div class="font-size-16 text-grey-darken-3 font-weight-500 mb-2">{{ label }}</div>
        <div v-if="oldFacility" class="bg-grey-lighten-4 pa-3 my-2">
          <v-row>
            <v-col cols="4">
              <div class="text-grey-darken-2">Previous facility:</div>
            </v-col>
            <v-col cols="8">
              <div class="mb-2">
                <router-link
                  :to="{ name: 'facilities-edit', params: { id: stop.facility?.id } }"
                  class="text-uppercase subheading font-weight-medium text-primary d-block text-decoration-none"
                  data-qa="facility-name"
                  target="_blank"
                >
                  {{ oldFacility?.name }}
                </router-link>
              </div>
              <div class="text-grey-darken-2 font-size-12" data-qa="popup-facility-address">
                {{ oldFacility?.addressLine }},
                {{ oldFacility?.address }}
              </div>
            </v-col>
          </v-row>
          <v-row v-if="lastEdited" class="mt-2">
            <v-col cols="4">
              <div class="text-grey-darken-2">Last stop editor:</div>
            </v-col>
            <v-col cols="8">
              <div class="font-weight-medium text-primary">{{ lastEdited.name }}</div>
            </v-col>
          </v-row>
        </div>
        <div v-if="updatedFreights.length > 0" class="bg-grey-lighten-4 pa-3 my-2">
          <div class="text-grey-darken-2 font-weight-500 mb-3">Freight changes</div>
          <v-row class="pb-0">
            <v-col>
              <div class="text-grey-darken-2">Previous:</div>
            </v-col>
            <v-col>
              <div class="text-grey-darken-2">Updated:</div>
            </v-col>
          </v-row>
          <v-row v-for="freight in updatedFreights" :key="freight.id" class="mt-0">
            <v-col>
              <div class="text-grey-darken-2 font-weight-500" data-qa="modal-previous-freight-data">
                #{{ freight.number }}: {{ freight.oldQuantity || freight.quantity }} {{ freight.type }},
                {{ freight.oldWeight || freight.weight }} lbs
              </div>
            </v-col>
            <v-col>
              <div class="text-grey-darken-2 font-weight-500" data-qa="modal-updated-freight-data">
                #{{ freight.number }}:
                <span :class="{ 'text-warning': freight.oldQuantity }">
                  {{ freight.quantity }}
                </span>
                {{ freight.type }}, <span :class="{ 'text-warning': freight.oldWeight }">{{ freight.weight }}</span> lbs
              </div>
            </v-col>
          </v-row>
        </div>
      </v-card-text>
    </v-card>
  </v-menu>
</template>

<style lang="scss" scoped></style>
